import React from 'react';
import clsx from 'clsx';
import {
  notEmptyString,
} from '@populateui/utils';
import Button from 'pages/Dashboard/components/Button';
import {
  SupportSqlPromptSuggestion,
} from 'dtos';

type Props = {
  reason?: string | null;
  triggerStage?: string | null;
  suggestions?: SupportSqlPromptSuggestion[] | null;
  onUseSuggestion: (suggestedPrompt: string) => void;
};

function getRecoveryHeading(triggerStage?: string | null) {
  if (triggerStage?.toLowerCase() === 'execution') {
    return 'Try one of these improved prompts';
  }
  return 'This request may need to be clarified';
}

function getRecoveryDescription(triggerStage?: string | null) {
  if (triggerStage?.toLowerCase() === 'execution') {
    return 'This query ran, but no matching results were returned. Try one of these variations.';
  }
  return 'Try a more specific version of your question to improve the next attempt.';
}

export default function SupportSqlPromptRecoverySection({
  reason,
  triggerStage,
  suggestions,
  onUseSuggestion,
}: Props) {
  const visibleSuggestions = (suggestions ?? [])
    .filter((item) => notEmptyString(item?.suggestedPrompt))
    .slice(0, 5);

  return visibleSuggestions.length === 0 ? null : (
    <div className="rounded-xl border border-sky-200 bg-sky-50/80 p-5 md:p-6">
      <div className="mb-4 flex flex-col gap-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
          Prompt Recovery
        </p>
        <h4 className="text-base font-semibold text-slate-900">
          {getRecoveryHeading(triggerStage)}
        </h4>
        <p className="text-sm leading-6 text-slate-600">
          {notEmptyString(reason) ? reason : getRecoveryDescription(triggerStage)}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {visibleSuggestions.map((suggestion) => {
          const prompt = suggestion.suggestedPrompt?.trim() ?? '';
          return (
            <div
              key={`${prompt}-${suggestion.category ?? suggestion.whyBetter ?? ''}`}
              className={clsx(
                'rounded-lg border border-sky-100 bg-white p-4 shadow-sm',
                'flex flex-col gap-3 md:flex-row md:items-start md:justify-between',
              )}
            >
              <div className="flex flex-col gap-2 min-w-0">
                <p className="text-sm font-medium leading-6 text-slate-900 whitespace-pre-wrap">
                  {prompt}
                </p>
                {notEmptyString(suggestion.whyBetter) && (
                  <p className="text-xs leading-5 text-slate-500">
                    {suggestion.whyBetter}
                  </p>
                )}
              </div>
              <Button
                variant="contained"
                color="primary"
                onClick={() => onUseSuggestion(prompt)}
                className="!bg-slate-900 hover:!bg-slate-800 !text-white !rounded-lg !px-4 !py-2 !w-full md:!w-auto md:min-w-[9.5rem]"
                aria-label={`Use prompt: ${prompt}`}
              >
                Use prompt
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
